/**
 * useSessionTimer - 테이블 세션 타이머 훅
 *
 * 각 sessionToken 을 독립된 generation 으로 다루며, 한 generation 에서 처음
 * 관찰한 종료 상태만 latch 한다. 종료 컨텍스트의 영속화·세션 정리·화면 이동은
 * 상위 종료 조정자가 담당한다.
 *
 * @see Issue #220 - 세션 만료 타이머 UI
 * @see Issue #221 - 세션 연장/종료 UI
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { endSession, extendSession, getSession } from '@/lib/api/qr';
import type { TableSessionResource } from '@/lib/api/types';
import {
  createSessionTerminalContext,
  mapApiErrorToTerminal,
  mapSessionResourceToTerminal,
  type SessionTerminalContext,
  type SessionTerminalSource,
} from '@/lib/sessionTerminal';
import { ApiClientError } from '@/lib/api/client';
import { syncTableSessionRecoveryExpiry } from '@/lib/tableContext';
import { logger } from '@/utils/logger';

const POLL_INTERVAL_MS = 60_000;
const COUNTDOWN_INTERVAL_MS = 1_000;
const WARNING_THRESHOLD_SECONDS = 300;

export type SessionEndResult =
  | {
      kind: 'manual-ended';
      success: true;
      message: string;
      sessionId: number;
    }
  | {
      kind: 'rejected';
      success: false;
      message: string;
    }
  | {
      kind: 'terminal';
      success: false;
      message: string;
      terminal: SessionTerminalContext;
    };

export interface UseSessionTimerReturn {
  remainingSeconds: number;
  isWarning: boolean;
  isExpired: boolean;
  isActive: boolean;
  extendCount: number;
  maxExtendCount: number;
  canExtendMore: boolean;
  handleExtend: () => Promise<{ success: boolean; message: string }>;
  handleEnd: () => Promise<SessionEndResult>;
  isActionLoading: boolean;
  isLoading: boolean;
  error: string | null;
  terminal: SessionTerminalContext | null;
  retry: () => Promise<void>;
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

export function useSessionTimer(sessionToken: string | null): UseSessionTimerReturn {
  const [session, setSession] = useState<TableSessionResource | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [isLoading, setIsLoading] = useState(Boolean(sessionToken));
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [terminal, setTerminal] = useState<SessionTerminalContext | null>(null);

  const tokenRef = useRef<string | null>(sessionToken);
  const initializedTokenRef = useRef<string | null | undefined>(undefined);
  const generationRef = useRef(0);
  const nextFetchIdRef = useRef(0);
  const lastSettledFetchIdRef = useRef(0);
  const terminalLatchRef = useRef<SessionTerminalContext | null>(null);
  const lifecycleStoppedRef = useRef(false);
  const expiresAtRef = useRef<string | null>(null);
  const sessionRef = useRef<TableSessionResource | null>(null);
  const controllersRef = useRef(new Set<AbortController>());
  const commandInFlightRef = useRef<{
    controller: AbortController;
    generation: number;
  } | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  tokenRef.current = sessionToken;

  const calcRemaining = useCallback((expiresAt: string): number => {
    return Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
  }, []);

  const clearIntervals = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (countdownIntervalRef.current !== null) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const abortRequests = useCallback(() => {
    for (const controller of controllersRef.current) {
      controller.abort();
    }
    controllersRef.current.clear();
  }, []);

  const isCurrentGeneration = useCallback((generation: number): boolean => {
    return generationRef.current === generation;
  }, []);

  const latchTerminal = useCallback((
    candidate: SessionTerminalContext,
    generation: number,
  ): SessionTerminalContext | null => {
    if (
      !isCurrentGeneration(generation) ||
      lifecycleStoppedRef.current ||
      terminalLatchRef.current
    ) {
      return terminalLatchRef.current;
    }

    terminalLatchRef.current = candidate;
    lifecycleStoppedRef.current = true;
    commandInFlightRef.current = null;
    expiresAtRef.current = null;
    clearIntervals();
    abortRequests();
    setRemainingSeconds(0);
    setIsLoading(false);
    setIsActionLoading(false);
    setError(null);
    setTerminal(candidate);
    return candidate;
  }, [abortRequests, clearIntervals, isCurrentGeneration]);

  const latchCountdownTerminal = useCallback((
    generation: number,
  ): SessionTerminalContext | null => {
    const currentSession = sessionRef.current;
    return latchTerminal(
      createSessionTerminalContext({
        reason: 'expired',
        source: 'countdown',
        httpStatus: null,
        serverStatus: 'expired',
        sessionId: currentSession?.id ?? null,
      }),
      generation,
    );
  }, [latchTerminal]);

  const ensureCountdown = useCallback((generation: number) => {
    if (countdownIntervalRef.current !== null || lifecycleStoppedRef.current) return;

    countdownIntervalRef.current = setInterval(() => {
      if (!isCurrentGeneration(generation) || lifecycleStoppedRef.current) return;

      const expiresAt = expiresAtRef.current;
      if (!expiresAt) return;
      const nextRemaining = calcRemaining(expiresAt);
      if (nextRemaining <= 0) {
        latchCountdownTerminal(generation);
        return;
      }
      setRemainingSeconds(nextRemaining);
    }, COUNTDOWN_INTERVAL_MS);
  }, [calcRemaining, isCurrentGeneration, latchCountdownTerminal]);

  const applyActiveSession = useCallback((
    data: TableSessionResource,
    generation: number,
    token: string,
  ) => {
    if (!isCurrentGeneration(generation) || lifecycleStoppedRef.current) return;

    syncTableSessionRecoveryExpiry({
      token,
      sessionId: data.id,
      expiresAt: data.expires_at,
    });
    sessionRef.current = data;
    expiresAtRef.current = data.expires_at;
    setSession(data);
    setError(null);

    const nextRemaining = calcRemaining(data.expires_at);
    if (nextRemaining <= 0) {
      latchCountdownTerminal(generation);
      return;
    }

    setRemainingSeconds(nextRemaining);
    ensureCountdown(generation);
  }, [calcRemaining, ensureCountdown, isCurrentGeneration, latchCountdownTerminal]);

  const beginRequest = useCallback((): AbortController => {
    const controller = new AbortController();
    controllersRef.current.add(controller);
    return controller;
  }, []);

  const fetchForGeneration = useCallback(async (
    token: string,
    generation: number,
    source: Extract<SessionTerminalSource, 'initial_fetch' | 'poll'>,
  ): Promise<void> => {
    if (!isCurrentGeneration(generation) || lifecycleStoppedRef.current) return;

    const fetchId = nextFetchIdRef.current + 1;
    nextFetchIdRef.current = fetchId;
    const controller = beginRequest();
    try {
      const response = await getSession(token, controller.signal);
      if (!isCurrentGeneration(generation) || lifecycleStoppedRef.current) return;
      if (fetchId <= lastSettledFetchIdRef.current) return;
      lastSettledFetchIdRef.current = fetchId;

      const terminalContext = mapSessionResourceToTerminal(response.data, source);
      if (terminalContext) {
        sessionRef.current = response.data;
        setSession(response.data);
        latchTerminal(terminalContext, generation);
        return;
      }

      applyActiveSession(response.data, generation, token);
    } catch (requestError) {
      if (
        isAbortError(requestError) ||
        !isCurrentGeneration(generation) ||
        lifecycleStoppedRef.current
      ) {
        return;
      }
      if (fetchId <= lastSettledFetchIdRef.current) return;
      lastSettledFetchIdRef.current = fetchId;

      const terminalContext = mapApiErrorToTerminal(requestError, source);
      if (terminalContext) {
        latchTerminal(terminalContext, generation);
        return;
      }

      logger.error('[useSessionTimer] 세션 조회 실패:', requestError);
      setError(
        requestError instanceof ApiClientError
          ? requestError.message
          : '세션 정보를 불러올 수 없습니다.',
      );
    } finally {
      controllersRef.current.delete(controller);
      if (
        source === 'initial_fetch' &&
        isCurrentGeneration(generation) &&
        !terminalLatchRef.current
      ) {
        setIsLoading(false);
      }
    }
  }, [applyActiveSession, beginRequest, isCurrentGeneration, latchTerminal]);

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    initializedTokenRef.current = sessionToken;
    terminalLatchRef.current = null;
    lifecycleStoppedRef.current = false;
    commandInFlightRef.current = null;
    nextFetchIdRef.current = 0;
    lastSettledFetchIdRef.current = 0;
    sessionRef.current = null;
    expiresAtRef.current = null;
    abortRequests();
    clearIntervals();

    setSession(null);
    setRemainingSeconds(0);
    setTerminal(null);
    setError(null);
    setIsActionLoading(false);
    setIsLoading(Boolean(sessionToken));

    if (sessionToken) {
      void fetchForGeneration(sessionToken, generation, 'initial_fetch');
      pollIntervalRef.current = setInterval(() => {
        if (!lifecycleStoppedRef.current && isCurrentGeneration(generation)) {
          void fetchForGeneration(sessionToken, generation, 'poll');
        }
      }, POLL_INTERVAL_MS);
    }

    return () => {
      if (isCurrentGeneration(generation)) {
        generationRef.current += 1;
      }
      lifecycleStoppedRef.current = true;
      commandInFlightRef.current = null;
      abortRequests();
      clearIntervals();
    };
  }, [
    abortRequests,
    clearIntervals,
    fetchForGeneration,
    isCurrentGeneration,
    sessionToken,
  ]);

  const retry = useCallback(async (): Promise<void> => {
    const token = tokenRef.current;
    const generation = generationRef.current;
    if (
      !token ||
      initializedTokenRef.current !== token ||
      lifecycleStoppedRef.current ||
      terminalLatchRef.current
    ) {
      return;
    }

    setIsLoading(true);
    try {
      await fetchForGeneration(
        token,
        generation,
        sessionRef.current ? 'poll' : 'initial_fetch',
      );
    } finally {
      if (isCurrentGeneration(generation) && !terminalLatchRef.current) {
        setIsLoading(false);
      }
    }
  }, [fetchForGeneration, isCurrentGeneration]);

  const handleExtend = useCallback(async (): Promise<{
    success: boolean;
    message: string;
  }> => {
    const token = tokenRef.current;
    const generation = generationRef.current;
    if (!token) {
      return { success: false, message: '세션 토큰이 없습니다.' };
    }
    if (initializedTokenRef.current !== token) {
      return { success: false, message: '세션을 전환하는 중입니다.' };
    }
    if (lifecycleStoppedRef.current) {
      return { success: false, message: '세션이 종료되었습니다.' };
    }
    if (commandInFlightRef.current) {
      return { success: false, message: '다른 세션 요청을 처리하고 있습니다.' };
    }

    const controller = beginRequest();
    commandInFlightRef.current = { controller, generation };
    setIsActionLoading(true);
    try {
      const response = await extendSession(token, controller.signal);
      if (!isCurrentGeneration(generation) || lifecycleStoppedRef.current) {
        return { success: false, message: '세션이 변경되었습니다.' };
      }

      const terminalContext = mapSessionResourceToTerminal(response.data, 'extend');
      if (terminalContext) {
        latchTerminal(terminalContext, generation);
        return { success: false, message: response.message };
      }

      // 사용자 명령의 서버 성공은 조회 시작 순서와 무관하게 권위가 있다.
      // 현재까지 시작된 fetch 를 모두 stale 처리해 늦은 조회 rollback 도 막는다.
      lastSettledFetchIdRef.current = nextFetchIdRef.current;
      applyActiveSession(response.data, generation, token);
      return { success: true, message: response.message };
    } catch (requestError) {
      if (
        isAbortError(requestError) ||
        !isCurrentGeneration(generation) ||
        lifecycleStoppedRef.current
      ) {
        return { success: false, message: '요청이 취소되었습니다.' };
      }

      const terminalContext = mapApiErrorToTerminal(requestError, 'extend');
      if (terminalContext) {
        latchTerminal(terminalContext, generation);
        return {
          success: false,
          message: requestError instanceof Error
            ? requestError.message
            : '세션이 종료되었습니다.',
        };
      }

      const message = requestError instanceof ApiClientError
        ? requestError.message
        : '세션 연장에 실패했습니다.';
      logger.error('[useSessionTimer] 세션 연장 실패:', requestError);
      return { success: false, message };
    } finally {
      controllersRef.current.delete(controller);
      const ownsCommandFlight =
        commandInFlightRef.current?.controller === controller;
      if (ownsCommandFlight) {
        commandInFlightRef.current = null;
      }
      if (
        ownsCommandFlight &&
        isCurrentGeneration(generation) &&
        !terminalLatchRef.current
      ) {
        setIsActionLoading(false);
      }
    }
  }, [
    applyActiveSession,
    beginRequest,
    isCurrentGeneration,
    latchTerminal,
  ]);

  const handleEnd = useCallback(async (): Promise<SessionEndResult> => {
    const token = tokenRef.current;
    const generation = generationRef.current;
    if (!token) {
      return {
        kind: 'rejected',
        success: false,
        message: '세션 토큰이 없습니다.',
      };
    }
    if (initializedTokenRef.current !== token) {
      return {
        kind: 'rejected',
        success: false,
        message: '세션을 전환하는 중입니다.',
      };
    }
    if (terminalLatchRef.current) {
      return {
        kind: 'terminal',
        success: false,
        message: '세션이 종료되었습니다.',
        terminal: terminalLatchRef.current,
      };
    }
    if (lifecycleStoppedRef.current) {
      return {
        kind: 'rejected',
        success: false,
        message: '세션이 이미 종료되었습니다.',
      };
    }
    if (commandInFlightRef.current) {
      return {
        kind: 'rejected',
        success: false,
        message: '다른 세션 요청을 처리하고 있습니다.',
      };
    }

    const controller = beginRequest();
    commandInFlightRef.current = { controller, generation };
    setIsActionLoading(true);
    try {
      const response = await endSession(token, controller.signal);
      if (!isCurrentGeneration(generation)) {
        return {
          kind: 'rejected',
          success: false,
          message: '세션이 변경되었습니다.',
        };
      }
      if (terminalLatchRef.current) {
        return {
          kind: 'terminal',
          success: false,
          message: response.message,
          terminal: terminalLatchRef.current,
        };
      }

      // 수동 종료 200은 poll 순서와 무관한 서버 권위 성공이다.
      lastSettledFetchIdRef.current = nextFetchIdRef.current;
      lifecycleStoppedRef.current = true;
      clearIntervals();
      abortRequests();
      sessionRef.current = response.data;
      expiresAtRef.current = null;
      setSession(response.data);
      setRemainingSeconds(0);
      setError(null);
      return {
        kind: 'manual-ended',
        success: true,
        message: response.message,
        sessionId: response.data.id,
      };
    } catch (requestError) {
      if (!isCurrentGeneration(generation)) {
        return {
          kind: 'rejected',
          success: false,
          message: '세션이 변경되었습니다.',
        };
      }
      if (isAbortError(requestError) || lifecycleStoppedRef.current) {
        const latched = terminalLatchRef.current;
        if (latched) {
          return {
            kind: 'terminal',
            success: false,
            message: '세션이 종료되었습니다.',
            terminal: latched,
          };
        }
        return {
          kind: 'rejected',
          success: false,
          message: '요청이 취소되었습니다.',
        };
      }

      const terminalContext = mapApiErrorToTerminal(requestError, 'manual_end');
      if (terminalContext) {
        const latched = latchTerminal(terminalContext, generation) ?? terminalContext;
        return {
          kind: 'terminal',
          success: false,
          message: requestError instanceof Error
            ? requestError.message
            : '세션이 종료되었습니다.',
          terminal: latched,
        };
      }

      const message = requestError instanceof ApiClientError
        ? requestError.message
        : '세션 종료에 실패했습니다.';
      logger.error('[useSessionTimer] 세션 종료 실패:', requestError);
      return { kind: 'rejected', success: false, message };
    } finally {
      controllersRef.current.delete(controller);
      const ownsCommandFlight =
        commandInFlightRef.current?.controller === controller;
      if (ownsCommandFlight) {
        commandInFlightRef.current = null;
      }
      if (
        ownsCommandFlight &&
        isCurrentGeneration(generation) &&
        !terminalLatchRef.current
      ) {
        setIsActionLoading(false);
      }
    }
  }, [
    abortRequests,
    beginRequest,
    clearIntervals,
    isCurrentGeneration,
    latchTerminal,
  ]);

  const lifecycleMatchesToken = initializedTokenRef.current === sessionToken;
  const exposedSession = lifecycleMatchesToken ? session : null;
  const exposedTerminal = lifecycleMatchesToken ? terminal : null;
  const exposedRemainingSeconds = lifecycleMatchesToken ? remainingSeconds : 0;
  const isWarning =
    exposedRemainingSeconds > 0 &&
    exposedRemainingSeconds <= WARNING_THRESHOLD_SECONDS;
  const isExpired =
    exposedTerminal !== null ||
    (exposedSession !== null && (
      exposedRemainingSeconds <= 0 ||
      exposedSession.is_expired ||
      exposedSession.status !== 'active' ||
      !exposedSession.is_active
    ));
  const isActive =
    exposedSession !== null &&
    exposedSession.is_active &&
    !isExpired &&
    !lifecycleStoppedRef.current;

  return {
    remainingSeconds: Math.max(0, exposedRemainingSeconds),
    isWarning,
    isExpired,
    isActive,
    extendCount: exposedSession?.extend_count ?? 0,
    maxExtendCount: exposedSession?.max_extend_count ?? 3,
    canExtendMore: exposedSession?.can_extend_more ?? false,
    handleExtend,
    handleEnd,
    isActionLoading: lifecycleMatchesToken ? isActionLoading : false,
    isLoading: lifecycleMatchesToken ? isLoading : Boolean(sessionToken),
    error: lifecycleMatchesToken ? error : null,
    terminal: exposedTerminal,
    retry,
  };
}
